import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from './SnackbarContext';
import config from '../config';
import axios from 'axios';

const useStyles = makeStyles(() => ({
    rootContainer: {
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'white',
        overflow: 'hidden',
        borderRadius: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Nunito, Poppins, Open Sans, Inter, sans-serif',
        padding: '20px',
        boxSizing: 'border-box',
    },
    title: {
        color: '#C084FC',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '600',
        lineHeight: '19.10px',
        wordWrap: 'break-word',
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        color: '#989898',
        fontSize: 15.28,
        fontFamily: 'Inter',
        fontWeight: '500',
        wordWrap: 'break-word',
        marginBottom: 40,
        textAlign: 'center',
    },
    formContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        '@media (max-width: 768px)': {
            width: '100%',
        },
    },
    label: {
        color: '#C084FC',
        fontSize: 16,
        fontFamily: 'Open Sans',
        fontWeight: '600',
        lineHeight: '19.10px',
        wordWrap: 'break-word',
        marginBottom: 8,
    },
    inputContainer: {
        position: 'relative',
        width: '100%',
    },
    input: {
        width: '100%',
        height: 50,
        borderRadius: 10,
        border: '1px solid black',
        outline: 'none',
        padding: '0 15px',
        fontSize: 16,
        fontFamily: 'Nunito',
        fontWeight: '500',
        boxSizing: 'border-box',
        '&:focus': {
            border: '2px solid #C084FC',
        },
    },
    passwordDots: {
        position: 'absolute',
        left: 15,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        gap: 5,
        pointerEvents: 'none',
    },
    dot: {
        width: 5.73,
        height: 5.73,
        background: '#545454',
        borderRadius: '50%',
    },
    eyeIcon: {
        position: 'absolute',
        right: 15,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 19.10,
        height: 19.10,
        cursor: 'pointer',
        background: 'black',
        mask: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z\'/%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'3\'/%3E%3C/svg%3E") no-repeat center',
        maskSize: 'contain',
    },
    submitButton: {
        width: '100%',
        height: 51,
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
        borderRadius: 10.40,
        border: 'none',
        color: 'white',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '700',
        cursor: 'pointer',
        marginTop: 20,
        '&:hover': {
            background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
        },
        '&:disabled': {
            background: '#E0E0E0',
            color: '#9E9E9E',
            cursor: 'not-allowed',
        },
    },
    errorText: {
        color: '#f44336',
        fontSize: 14,
        textAlign: 'center',
        marginTop: 8,
        fontFamily: 'Nunito',
    },
}));

const PasswordReset = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const { userData, role } = location.state || {};

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        setIsLoading(true);
        try {
            // Call the password update API
            const response = await axios.post(`${config.API_BASE_URL}/update-password`, {
                user_id: userData.user_id,
                new_password: password
            });

            if (response.data.message) {
                showSnackbar('Password updated successfully', 'success');
                
                // Navigate to success screen
                setTimeout(() => {
                    navigate('/password-success', {
                        state: {
                            userData,
                            role
                        }
                    });
                }, 1000);
            }
        } catch (error) {
            setError('Failed to update password. Please try again.');
            showSnackbar('Failed to update password', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={classes.rootContainer}>
            <div className={classes.title}>
                Set a password
            </div>
            <div className={classes.subtitle}>
                Create your password.
            </div>

            <form className={classes.formContainer} onSubmit={handleSubmit}>
                <div>
                    <div className={classes.label}>Password</div>
                    <div className={classes.inputContainer}>
                        <input
                            className={classes.input}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter new password"
                            disabled={isLoading}
                        />
                        {!showPassword && password && (
                            <div className={classes.passwordDots}>
                                {Array.from({ length: Math.min(password.length, 11) }).map((_, index) => (
                                    <div key={index} className={classes.dot} />
                                ))}
                            </div>
                        )}
                        <div 
                            className={classes.eyeIcon}
                            onClick={() => setShowPassword(!showPassword)}
                        />
                    </div>
                </div>

                <div>
                    <div className={classes.label}>Confirm Password</div>
                    <div className={classes.inputContainer}>
                        <input
                            className={classes.input}
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            disabled={isLoading}
                        />
                        {!showConfirmPassword && confirmPassword && (
                            <div className={classes.passwordDots}>
                                {Array.from({ length: Math.min(confirmPassword.length, 11) }).map((_, index) => (
                                    <div key={index} className={classes.dot} />
                                ))}
                            </div>
                        )}
                        <div 
                            className={classes.eyeIcon}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    className={classes.submitButton}
                    disabled={isLoading || !password || !confirmPassword}
                >
                    {isLoading ? 'Updating...' : 'Update Password'}
                </button>

                {error && (
                    <div className={classes.errorText}>
                        {error}
                    </div>
                )}
            </form>
        </div>
    );
};

export default PasswordReset;
